/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useState } from "react";
import api, { API, formatApiError } from "../lib/api";
import { Plus, X, ImageSquare } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_IMAGES = 3;
const MAX_DIMENSION = 1600; // px, long side
const JPEG_QUALITY = 0.8;

/**
 * Resize an image file in-browser to at most MAX_DIMENSION on the long side,
 * re-encode as JPEG at ~80% quality. Shrinks typical phone photos from several
 * MB to ~200–400 KB, keeping mobile upload fast. Falls back to the original
 * file if anything throws (svg, tiny images, browser without canvas, etc.).
 */
async function compressImage(file) {
  try {
    if (!file.type.startsWith("image/")) return file;
    if (file.type === "image/gif") return file; // preserve animation
    const bmp = await createImageBitmap(file).catch(() => null);
    if (!bmp) return file;
    const { width, height } = bmp;
    const long = Math.max(width, height);
    const scale = long > MAX_DIMENSION ? MAX_DIMENSION / long : 1;
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bmp, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", JPEG_QUALITY));
    if (!blob) return file;
    // Only swap if the compressed version is smaller
    if (blob.size >= file.size) return file;
    return new File([blob], (file.name || "menu").replace(/\.[^.]+$/, "") + ".jpg", {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

/**
 * Compact multi-image uploader (0 → MAX_IMAGES).
 * Shows thumbnail row + one "Add" slot. Each thumb has an (x) to remove.
 *
 * Props:
 *   paths: string[]      — current storage paths
 *   onChange: (paths: string[]) => void
 *   testId: string       — data-testid prefix
 *   label?: string
 *   hint?: string
 */
export default function MenuImagesEditor({ paths = [], onChange, testId, label, hint }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const canAdd = paths.length < MAX_IMAGES;

  const pickFiles = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file
    if (files.length === 0) return;
    setUploading(true);
    const remaining = MAX_IMAGES - paths.length;
    const toUpload = files.slice(0, remaining);
    const uploaded = [];
    for (const f of toUpload) {
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`${f.name} is larger than 8 MB — skipped`);
        continue;
      }
      try {
        const compressed = await compressImage(f);
        const form = new FormData();
        form.append("file", compressed);
        const { data: res } = await api.post("/uploads/menu-image", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(res.path);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `Failed to upload ${f.name}`);
      }
    }
    if (uploaded.length) {
      onChange([...paths, ...uploaded].slice(0, MAX_IMAGES));
      toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} uploaded`);
    }
    setUploading(false);
  };

  const removeAt = (i) => {
    const next = paths.filter((_, idx) => idx !== i);
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {(label || hint) && (
        <div className="space-y-0.5">
          {label && <div className="text-sm font-medium">{label}</div>}
          {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {paths.map((p, i) => (
          <div
            key={p}
            className="relative h-20 w-20 rounded-lg overflow-hidden border border-border bg-muted group"
            data-testid={`${testId}-thumb-${i}`}
          >
            <img
              src={`${API}/files/${p}`}
              alt={`Menu ${i + 1}`}
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white grid place-items-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition"
              aria-label={`Remove image ${i + 1}`}
              data-testid={`${testId}-remove-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}

        {canAdd && (
          <button
            type="button"
            onClick={pickFiles}
            disabled={uploading}
            className={`h-20 w-20 rounded-lg border-2 border-dashed border-border grid place-items-center text-muted-foreground hover:border-primary/60 hover:text-primary transition ${
              uploading ? "opacity-60" : ""
            }`}
            data-testid={`${testId}-add-btn`}
            aria-label="Add image"
          >
            {uploading ? (
              <span className="text-xs">…</span>
            ) : paths.length === 0 ? (
              <div className="flex flex-col items-center gap-1">
                <ImageSquare weight="duotone" className="h-5 w-5" />
                <span className="text-[10px] uppercase tracking-wider">Add</span>
              </div>
            ) : (
              <Plus weight="bold" className="h-5 w-5" />
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onFiles}
        data-testid={`${testId}-input`}
      />

      {paths.length > 0 && (
        <div className="text-[11px] text-muted-foreground">
          {paths.length}/{MAX_IMAGES} image{paths.length === 1 ? "" : "s"} • compressed for fast mobile loading
        </div>
      )}
    </div>
  );
}
